"use client"

import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

import { FinanceTransactionStatus } from "@/features/finance/model/finance-types"
import type { InvestigationContext } from "@/features/finance/lib/finance-investigation-url"

export const INVESTIGATION_RECENTS_CAP = 6

/** What a remembered investigation was started from. Drives its icon and its kind label on the launcher. */
export enum InvestigationRecentKind {
  Transaction = "transaction",
  Journal = "journal",
  Account = "account",
  Fund = "fund",
  Activity = "activity",
  BudgetLine = "budgetLine",
  Department = "department",
  Ministry = "ministry",
  Group = "group",
  Member = "member",
  User = "user",
  Search = "search",
  MissingEvidence = "missingEvidence",
  Reversed = "reversed",
  Voided = "voided",
  Explorer = "explorer",
}

/**
 * One remembered investigation. Only what is needed to show and reopen it (kind, the label the user saw, the
 * href it opened): never amounts or record contents. The href is the same one the URL contract built, so a
 * reopen lands exactly where the first open did, and the server re-checks access and disclosure on arrival.
 */
export type InvestigationRecentEntry = {
  kind: InvestigationRecentKind
  label: string | null
  href: string
  openedAt: string
}

/** Recents belong to one workspace + branch: an investigation opened in one branch is not offered in another. */
export function investigationRecentsScopeKey(
  workspaceSlug: string | null | undefined,
  branchSlug: string | null | undefined,
): string | null {
  if (!workspaceSlug || !branchSlug) return null
  return `${workspaceSlug}:${branchSlug}`
}

/** The kind of an investigation context: the first anchor or facet it names, in the order the URL contract reads them. */
export function investigationRecentKind(context: InvestigationContext): InvestigationRecentKind {
  if (context.transactionId) return InvestigationRecentKind.Transaction
  if (context.journalEntryId) return InvestigationRecentKind.Journal
  if (context.accountingAccountId) return InvestigationRecentKind.Account
  if (context.fundId) return InvestigationRecentKind.Fund
  if (context.activityId) return InvestigationRecentKind.Activity
  if (context.budgetLineId) return InvestigationRecentKind.BudgetLine
  if (context.departmentId) return InvestigationRecentKind.Department
  if (context.ministryId) return InvestigationRecentKind.Ministry
  if (context.groupId) return InvestigationRecentKind.Group
  if (context.memberId) return InvestigationRecentKind.Member
  if (context.actorUserId) return InvestigationRecentKind.User
  if (context.search || context.transactionReference) return InvestigationRecentKind.Search
  if (context.hasEvidence === false) return InvestigationRecentKind.MissingEvidence
  if (context.status === FinanceTransactionStatus.Cancelled) return InvestigationRecentKind.Reversed
  if (context.status === FinanceTransactionStatus.Voided) return InvestigationRecentKind.Voided
  return InvestigationRecentKind.Explorer
}

/** The entry to remember for a context that was just opened at `href`. */
export function investigationRecentFromContext(
  context: InvestigationContext,
  href: string,
  openedAt: Date = new Date(),
): InvestigationRecentEntry {
  const label = context.label ?? context.search ?? context.transactionReference ?? null
  return { kind: investigationRecentKind(context), label, href, openedAt: openedAt.toISOString() }
}

type InvestigationRecentsState = {
  byScope: Record<string, InvestigationRecentEntry[]>
  pushRecent: (scopeKey: string, entry: InvestigationRecentEntry) => void
  clearRecents: (scopeKey: string) => void
}

/** The audit launcher's "Recent investigations", kept on this device only (same pattern as the search recents). */
export const useInvestigationRecentsStore = create<InvestigationRecentsState>()(
  persist(
    (set) => ({
      byScope: {},
      pushRecent: (scopeKey, entry) =>
        set((state) => {
          const current = state.byScope[scopeKey] ?? []
          const kept: InvestigationRecentEntry = {
            kind: entry.kind,
            label: entry.label,
            href: entry.href,
            openedAt: entry.openedAt,
          }
          const next = [kept, ...current.filter((item) => item.href !== kept.href)].slice(0, INVESTIGATION_RECENTS_CAP)
          return { byScope: { ...state.byScope, [scopeKey]: next } }
        }),
      clearRecents: (scopeKey) =>
        set((state) => {
          const next = { ...state.byScope }
          delete next[scopeKey]
          return { byScope: next }
        }),
    }),
    {
      name: "tenant-finance-investigation-recents-v1",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ byScope: state.byScope }),
    },
  ),
)

const NO_RECENTS: InvestigationRecentEntry[] = []

export function selectInvestigationRecents(scopeKey: string | null) {
  return (state: InvestigationRecentsState): InvestigationRecentEntry[] =>
    scopeKey ? (state.byScope[scopeKey] ?? NO_RECENTS) : NO_RECENTS
}
